"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoutesWithWaypointsResponseSchema = exports.RoutedWaypointStopSchema = exports.RoutesWithWaypointsRequestSchema = exports.MAX_ROUTES_WITH_WAYPOINTS_STOPS = exports.DestinationRouteSchema = exports.RestAreaRoutePositionSchema = exports.CompactRouteResultSchema = exports.CompactRouteStopSchema = exports.SortStrategySchema = exports.GeneralRouteSummarySchema = exports.RouteWithWaypointResultSchema = exports.SimpleRouteResultSchema = exports.StepSchema = exports.RoutePlannerFormSchema = exports.RemainingTimeSecondsSchema = exports.MAX_WAYPOINTS = void 0;
const zod_1 = require("zod");
const geo_1 = require("./geo");
const rest_area_1 = require("./rest-area");
/** User-chosen via points between start and destination of a route search. */
exports.MAX_WAYPOINTS = 5;
/** Accepts numeric strings too — deployed clients send `remainingTime` either way. */
exports.RemainingTimeSecondsSchema = zod_1.z.coerce.number().positive().finite();
/** Route planner form. `start: null` is the driver's current position; a destination is required. */
exports.RoutePlannerFormSchema = zod_1.z.object({
    start: geo_1.AddressLocationSchema.nullable(),
    destination: geo_1.AddressLocationSchema.nullable().pipe(geo_1.AddressLocationSchema),
    waypoints: zod_1.z.array(geo_1.AddressLocationSchema).max(exports.MAX_WAYPOINTS),
    restTimeSeconds: exports.RemainingTimeSecondsSchema,
    avoidTolls: zod_1.z.boolean(),
});
const routeCoordinateSchema = zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()]);
exports.StepSchema = zod_1.z.object({
    duration: zod_1.z.number(),
    wayPoints: zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()]),
});
exports.SimpleRouteResultSchema = zod_1.z.object({
    routeCoordinates: zod_1.z.array(routeCoordinateSchema),
    steps: zod_1.z.array(exports.StepSchema),
    totalDuration: zod_1.z.number(),
    totalDistance: zod_1.z.number(),
    boundingBox: zod_1.z.tuple([zod_1.z.number(), zod_1.z.number(), zod_1.z.number(), zod_1.z.number()]).optional(),
    legEndIndices: zod_1.z.array(zod_1.z.number()).optional(),
});
exports.RouteWithWaypointResultSchema = exports.SimpleRouteResultSchema.extend({
    durationToWaypoint: zod_1.z.number(),
    distanceToWaypoint: zod_1.z.number(),
    waypointIndex: zod_1.z.number(),
});
exports.GeneralRouteSummarySchema = zod_1.z.object({
    /** Meters. */
    totalDistance: zod_1.z.number(),
    /** Seconds. */
    travelTime: zod_1.z.number(),
    parkingAreas: zod_1.z.number(),
    parkingSpaces: zod_1.z.number(),
    /** Seconds. */
    maxRestTime: zod_1.z.number(),
    /** Truck speed cap in m/s. */
    speed: zod_1.z.number(),
    /** Corridor radius in meters the stops were found with. */
    corridor: zod_1.z.number(),
    /** Meters before the rest point the search window reached. */
    resultWindowMetersBefore: zod_1.z.number(),
    /** Where the remaining driving time runs out; absent when the destination is reachable. */
    restPoint: routeCoordinateSchema.optional(),
});
exports.SortStrategySchema = zod_1.z.enum([
    'general-optimization',
    'general-optimization-prio-time-after-rest-area',
    'general-optimization-prio-extra-time',
    /** Closest to the rest-time limit without exceeding it. */
    'max-rest-time-usage',
    /** Least extra travel time compared to the direct route. */
    'min-extra-time',
    'time-to-parking-area',
]);
/**
 * A rest area routed as a stop between start and destination, without the rest area's own
 * details — each consumer adds its rest-area type as `locationProperties`. Durations in seconds,
 * distances in meters.
 */
exports.CompactRouteStopSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    distanceToParkingArea: zod_1.z.number(),
    timeToParkingArea: zod_1.z.number(),
    estimatedTimeOfArrival: zod_1.z.string(),
    timeAfterParkingArea: zod_1.z.number(),
    /** start → stop → destination. */
    totalRouteDuration: zod_1.z.number(),
    /** Share of the remaining drive time used to reach the stop, 0–100. */
    restTimeLimitUsagePercent: zod_1.z.number(),
    /** Compared to the direct route. */
    extraTime: zod_1.z.number(),
    /** Compared to the direct route. */
    extraDistance: zod_1.z.number(),
    coordinates: routeCoordinateSchema,
    route: exports.RouteWithWaypointResultSchema,
    /** At `estimatedTimeOfArrival`, Europe/Berlin. */
    predictedAvailabilityAtEta: rest_area_1.SiteAvailabilityStatusSchema,
});
/** `getRoutesWithStopOnRoute` with `includeStopDetails: false`. */
exports.CompactRouteResultSchema = zod_1.z.object({
    routeSummary: exports.GeneralRouteSummarySchema,
    routeStops: zod_1.z.array(exports.CompactRouteStopSchema),
    /** Corridor stops not routed yet — the "load more" pool. */
    additionalStopIds: zod_1.z.array(zod_1.z.string()),
    /**
     * Prediction at the approximated ETA per id in `additionalStopIds`, so the arrival filter covers
     * the whole corridor. Ids without an approximation are omitted ("unknown").
     */
    additionalStopPredictions: zod_1.z.record(zod_1.z.string(), rest_area_1.SiteAvailabilityStatusSchema),
    /** The stop to badge as recommended. Omitted when no stop fits the remaining driving time. */
    recommendedStopId: zod_1.z.string().optional(),
});
/**
 * Where a rest area lies along a planned route, without the rest area's own details — each
 * consumer adds its rest-area type. Distances in meters, durations in seconds.
 */
exports.RestAreaRoutePositionSchema = zod_1.z.object({
    /** Along the route, including the off-route leg to the rest area. */
    distanceFromStart: zod_1.z.number(),
    /** Air distance to the nearest route coordinate. */
    distanceFromRoute: zod_1.z.number(),
    estimatedArrival: zod_1.z.string(),
    timeToStop: zod_1.z.number(),
    /** At `estimatedArrival`, Europe/Berlin. */
    predictedAvailabilityAtEta: rest_area_1.SiteAvailabilityStatusSchema,
});
/**
 * `getRouteWithRestAreas` without its rest areas — each consumer adds `restAreasAlongRoute` with
 * its rest-area type.
 */
exports.DestinationRouteSchema = exports.SimpleRouteResultSchema.pick({
    routeCoordinates: true,
    totalDuration: true,
    totalDistance: true,
    boundingBox: true,
});
exports.MAX_ROUTES_WITH_WAYPOINTS_STOPS = 10;
exports.RoutesWithWaypointsRequestSchema = zod_1.z.object({
    start: geo_1.coordTupleSchema,
    end: geo_1.coordTupleSchema,
    stops: zod_1.z
        .array(zod_1.z.object({ id: zod_1.z.string().min(1), coordinate: geo_1.coordTupleSchema }))
        .min(1)
        .max(exports.MAX_ROUTES_WITH_WAYPOINTS_STOPS),
    /** The route's own via points; each stop is routed into the leg it lies on. */
    waypoints: zod_1.z.array(geo_1.coordTupleSchema).max(exports.MAX_WAYPOINTS).default([]),
    avoidTolls: zod_1.z.boolean().default(false),
    /** False returns routes without points — durations and distances only. */
    includeGeometry: zod_1.z.boolean().default(true),
});
/** One entry per requested stop, in request order; `null` where TomTom could not route that stop. */
exports.RoutedWaypointStopSchema = zod_1.z.object({
    stopId: zod_1.z.string(),
    route: exports.RouteWithWaypointResultSchema,
    predictedAvailabilityAtEta: rest_area_1.SiteAvailabilityStatusSchema,
});
exports.RoutesWithWaypointsResponseSchema = zod_1.z.array(exports.RoutedWaypointStopSchema.nullable());
//# sourceMappingURL=routing.js.map